import React, { useCallback, useEffect, useMemo, useState, createContext, useContext } from 'react';
// A lightweight signal that lets an in-page section temporarily SUPPRESS the
// global floating "Try Giga for free" CTA (rendered by Layout). The integration
// "plugs into your stack" beat owns its own primary "See all integrations"
// button, so while that beat is on screen we hide the competing floating CTA
// and bring it back for the following "Lives inside your chats" beat.
type FloatingCtaContextValue = {
  suppressed: boolean;
  // Register/clear a suppression. Returns nothing; callers flip it based on
  // their own scroll choreography.
  setSuppressed: (next: boolean) => void;
};
const FloatingCtaContext = createContext<FloatingCtaContextValue | null>(null);
export function FloatingCtaProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [suppressed, setSuppressedState] = useState(false);
  const setSuppressed = useCallback((next: boolean) => {
    setSuppressedState(next);
  }, []);
  const value = useMemo(() => ({
    suppressed,
    setSuppressed
  }), [suppressed, setSuppressed]);
  return <FloatingCtaContext.Provider value={value} data-id="element-3555" data-mp="3ugpr8usrn45p">
      {children}
    </FloatingCtaContext.Provider>;
}
// Read the current suppression flag (used by Layout's floating CTA).
export function useFloatingCtaState() {
  const ctx = useContext(FloatingCtaContext);
  return ctx?.suppressed ?? false;
}
// Imperatively suppress the floating CTA while `active` is true, cleaning up on
// unmount. Safe to call when no provider is present (no-op).
export function useSuppressFloatingCta(active: boolean) {
  const ctx = useContext(FloatingCtaContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setSuppressed(active);
    return () => ctx.setSuppressed(false);
  }, [ctx, active]);
}